"use client";

import { useMemo, useState } from "react";
import { db } from "@/firebase/config";
import {
  doc,
  serverTimestamp,
  updateDoc,
} from "@/lib/firestore-instrumented";
import { useAuth } from "@/context/AuthContext";
import { PRIVACY_VERSION, TERMS_VERSION } from "@/lib/legalDocuments";
import { getLegalAcceptanceStatus } from "@/lib/legalAcceptance";

export default function LegalAcceptanceGate({ disabled = false, children }) {
  const { user, profile, profileLoading } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [acceptedProfile, setAcceptedProfile] = useState(null);

  const userRef = useMemo(() => {
    if (!user?.uid) return null;
    return doc(db, "users", user.uid);
  }, [user?.uid]);

  const effectiveProfile = acceptedProfile
    ? { ...(profile || {}), ...acceptedProfile }
    : profile;
  const acceptance = getLegalAcceptanceStatus(effectiveProfile || {});
  const shouldBlock =
    !disabled &&
    !profileLoading &&
    Boolean(effectiveProfile) &&
    Boolean(effectiveProfile.profileComplete) &&
    acceptance.needsAcceptance;

  const handleAccept = async () => {
    if (!userRef) return;
    setAccepting(true);
    setError("");
    try {
      const providerId = user?.providerData?.[0]?.providerId || "";
      const acceptedAt = new Date();
      await updateDoc(userRef, {
        termsAcceptedAt: serverTimestamp(),
        termsVersion: TERMS_VERSION,
        privacyAcknowledgedAt: serverTimestamp(),
        privacyVersion: PRIVACY_VERSION,
        legalAcceptedFrom: "legal-update-interstitial",
        legalAcceptedProviderId: providerId || null,
        updatedAt: serverTimestamp(),
      });
      setAcceptedProfile({
        termsAcceptedAt: acceptedAt,
        termsVersion: TERMS_VERSION,
        privacyAcknowledgedAt: acceptedAt,
        privacyVersion: PRIVACY_VERSION,
        legalAcceptedFrom: "legal-update-interstitial",
        legalAcceptedProviderId: providerId || null,
      });
      setAccepting(false);
    } catch (err) {
      console.warn("[legal] failed to save acceptance", err);
      setAccepting(false);
      setError("We couldn't save your acceptance. Please try again.");
    }
  };

  if (disabled || profileLoading || !shouldBlock) {
    return children;
  }

  return (
    <section className="edge-mobile">
      <div className="card square-mobile space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
            Updated legal terms
          </p>
          <h1 className="h2">Review Sproutly's Terms and Privacy Policy</h1>
          <p className="body">
            Before continuing, please review and accept the current Terms of Service and
            acknowledge the Privacy Policy.
          </p>
        </div>

        <div className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-1)] px-3 py-3 text-sm text-[var(--fg)]">
          <p>
            Current versions: Terms {TERMS_VERSION}, Privacy {PRIVACY_VERSION}.
          </p>
          <div className="mt-2 flex flex-wrap gap-3">
            <a
              href="/info/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--brand)] underline"
            >
              Open Terms
            </a>
            <a
              href="/info/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--brand)] underline"
            >
              Open Privacy Policy
            </a>
          </div>
        </div>

        {error ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            className="btn px-5"
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? "Saving..." : "Accept and continue"}
          </button>
        </div>
      </div>
    </section>
  );
}
